import { action } from 'typesafe-actions';

import { RequirementsNpaActionsEnum } from '../enums/RequirementsNpaActionsEnum';
import { RequirementsNpaSourceEnum } from '../enums/RequirementsNpaSourceEnum';
import { IRequirementsNpaFormRef } from '../interfaces/IRequirementsNpaForm';
import { IRequirementsNpaFilter } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementsNpaFilter';
import { IRequirementNpa } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { RequirementsModalModeEnum } from '../enums/RequirementsModalModeEnum';
import {
  IRequirementStructuralUnit,
  IRequirementStructuralUnitRaw,
} from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import {
  IRequirementsMandatory,
  IRequirementsMandatoryRaw,
} from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementsMandatory';
import { ICreateNpaRequest } from '../../../APIPackage/RequirementsNpaModule/interfaces/ICreateNpaRequest';
import { INotification } from '../../../CorePackage/NotificationModule/interfaces/INotification';
import { IRequirementsIdMap } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementsIdMap';
import { IRequirementNpaResponse } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpaResponse';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { IStructuresUnitsFilters } from '../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';

export const getNpaCountAction = (filter: IRequirementsNpaFilter) =>
  action(RequirementsNpaActionsEnum.GET_NPA_COUNT, { filter });
export const setNpaCountAction = (count: number) => action(RequirementsNpaActionsEnum.SET_NPA_COUNT, { count });
export const setNpaSourceAction = (source: RequirementsNpaSourceEnum | null) =>
  action(RequirementsNpaActionsEnum.SET_NPA_SOURCE, { source });
export const setFormRefAction = (formRef: IRequirementsNpaFormRef) =>
  action(RequirementsNpaActionsEnum.SET_FORM_REF, { formRef });
export const getNpaListAction = (filter: IRequirementsNpaFilter) =>
  action(RequirementsNpaActionsEnum.GET_NPA_LIST, { filter });
export const setNpaListAction = (data: IRequirementNpa[]) => action(RequirementsNpaActionsEnum.SET_NPA_LIST, { data });
export const setModalModeAction = (mode: RequirementsModalModeEnum) =>
  action(RequirementsNpaActionsEnum.SET_MODAL_MODE, { mode });
export const setSelectedNpaAction = (npa: IRequirementNpa | null) =>
  action(RequirementsNpaActionsEnum.SET_SELECTED_NPA, { npa });
export const getStructuralUnitListAction = (
  npaId: number,
  oivControlId: string | null,
  oivControlTypeId: string | null,
) => action(RequirementsNpaActionsEnum.GET_STRUCTURAL_UNIT_LIST, { npaId, oivControlId, oivControlTypeId });
export const setStructuralUnitListAction = (data: IRequirementStructuralUnit[]) =>
  action(RequirementsNpaActionsEnum.SET_STRUCTURAL_UNIT_LIST, { data });
export const getMandatoryListAction = (npaId: number, suId: number, filter: IRequirementsNpaFilter) =>
  action(RequirementsNpaActionsEnum.GET_MANDATORY_LIST, { npaId, suId, filter });
export const setMandatoryListAction = (suId: number, data: IRequirementsMandatory[]) =>
  action(RequirementsNpaActionsEnum.SET_MANDATORY_LIST, { suId, data });
export const addSelectedMandatoryAction = (mandatory: IRequirementsMandatory) =>
  action(RequirementsNpaActionsEnum.ADD_SELECTED_MANDATORY, { mandatory });
export const setSelectedMandatoryAction = (mandatory: IRequirementsMandatory[]) =>
  action(RequirementsNpaActionsEnum.SET_SELECTED_MANDATORY, { mandatory });
export const setIsMandatoryCreatedAction = (value: boolean) =>
  action(RequirementsNpaActionsEnum.SET_IS_MANDATORY_CREATED, { value });
export const resetModalAction = () => action(RequirementsNpaActionsEnum.RESET_MODAL_LISTS, {});
export const createNpaAction = (npa: ICreateNpaRequest, structuralUnits: IRequirementStructuralUnitRaw[]) =>
  action(RequirementsNpaActionsEnum.CREATE_NPA, { npa, structuralUnits });
export const createSusAction = (
  npaId: number,
  structuralUnits: IRequirementStructuralUnitRaw[],
  callback: (suId: number) => void,
) => action(RequirementsNpaActionsEnum.CREATE_SUS, { npaId, structuralUnits, callback });
export const createSusWithMandatoryAction = (
  npaId: number,
  knoVersionId: string,
  supervisionVersionId: string,
  structuresUnitsFilters: IStructuresUnitsFilters,
  units: IRequirementStructuralUnitRaw[],
  callback: (suId: number, mandatoryId: number) => void,
) =>
  action(RequirementsNpaActionsEnum.CREATE_SUS_WITH_MANDATORY, {
    npaId,
    knoVersionId,
    supervisionVersionId,
    ...structuresUnitsFilters,
    units,
    callback,
  });

export const setIsNpaStructureChanged = (value: boolean) =>
  ({ type: RequirementsNpaActionsEnum.SET_IS_NPA_STRUCTURE_CHANGED, payload: value } as const);
export const setSavedNpaAction = (npa: IRequirementNpa | null) =>
  action(RequirementsNpaActionsEnum.SET_SAVED_NPA, { npa });
export const editNpaAction = (npa: IRequirementNpa, structuralUnits: IRequirementStructuralUnitRaw[]) =>
  action(RequirementsNpaActionsEnum.EDIT_NPA, { npa, structuralUnits });
export const setOivControlIdAction = (id: string | null) =>
  action(RequirementsNpaActionsEnum.SET_OIV_CONTROL_ID, { id });
export const setOivControlTypeIdAction = (id: string | null) =>
  action(RequirementsNpaActionsEnum.SET_OIV_CONTROL_TYPE_ID, { id });
export const createStructuralUnitsAction = (units: IRequirementStructuralUnitRaw[], npaId: number) =>
  action(RequirementsNpaActionsEnum.CREATE_STRUCTURAL_UNITS, { units, npaId });
export const createMandatoryListAction = (
  mandatoryList: IRequirementsMandatoryRaw[],
  npaId: number,
  callback?: (mandatoryId: number) => void,
) => action(RequirementsNpaActionsEnum.CREATE_MANDATORY_LIST, { mandatoryList, npaId, callback });
export const addNoticeAction = (notice: INotification) =>
  action(RequirementsNpaActionsEnum.ADD_NPA_MODAL_NOTICE, { notice });
export const removeNoticeAction = (id: string) => action(RequirementsNpaActionsEnum.REMOVE_NPA_MODAL_NOTICE, { id });
export const removeAllNoticesAction = () => action(RequirementsNpaActionsEnum.REMOVE_ALL_NPA_MODAL_NOTICES, {});
export const showValidationNotice = () => action(RequirementsNpaActionsEnum.SHOW_VALIDATION_NOTICE, {});
export const setIsNpaCreatedAction = (value: boolean) =>
  action(RequirementsNpaActionsEnum.SET_IS_NPA_CREATED, { value });
export const setIsSuCreatedAction = (value: boolean) => action(RequirementsNpaActionsEnum.SET_IS_SU_CREATED, { value });
export const setIsNpaEditedAction = (value: boolean) => action(RequirementsNpaActionsEnum.SET_IS_NPA_EDITED, { value });
export const setIsStructuralUnitCreatedAction = (value: boolean) =>
  action(RequirementsNpaActionsEnum.SET_IS_STRUCTURAL_UNIT_CREATED, { value });
export const setIsNpaListDirty = (value: boolean) =>
  action(RequirementsNpaActionsEnum.SET_IS_NPA_LIST_DIRTY, { value });
export const setIsNpaEditedWithErrorAction = (value: boolean) =>
  action(RequirementsNpaActionsEnum.SET_IS_NPA_EDITED_WITH_ERROR, { value });
export const setIsListStructuralUnitLoaded = (value: boolean) =>
  action(RequirementsNpaActionsEnum.SET_IS_LIST_STRUCTURAL_UNIT_LOADED, { value });
export const setCreatedSelectedStructuralUnits = (units: IRequirementStructuralUnit[]) =>
  action(RequirementsNpaActionsEnum.SET_CREATED_SELECTED_STRUCTURAL_UNITS, { units });
export const addToStructuralUnitList = (units: IRequirementStructuralUnit[]) =>
  action(RequirementsNpaActionsEnum.ADD_TO_STRUCTURAL_UNIT_LIST, { units });
export const setIdMap = (value: IRequirementsIdMap | null) => action(RequirementsNpaActionsEnum.SET_ID_MAP, { value });
export const getNpaListNewAction = ({
  knoVersionId,
  supervisionVersionId,
  npaIds,
  mrIds,
}: {
  knoVersionId: string;
  supervisionVersionId: string;
  npaIds?: number[];
  mrIds?: number[];
}) => action(RequirementsNpaActionsEnum.GET_NPA_LIST_NEW, { knoVersionId, supervisionVersionId, npaIds, mrIds });
export const setNpaListNew = (data: IRequirementNpaResponse | null) =>
  action(RequirementsNpaActionsEnum.SET_NPA_LIST_NEW, { data });
export const getCurrentNpaAction = (npaId: number | undefined, knoVersionId: string, supervisionVersionId: string) =>
  action(RequirementsNpaActionsEnum.GET_CURRENT_NPA, { npaId, knoVersionId, supervisionVersionId });
export const setCurrentNpaAction = (data: IRequirementNpaResponse | null) =>
  action(RequirementsNpaActionsEnum.SET_CURRENT_NPA, { data });
export const requirementsNpaFindSelectOptionsByQuery = (query: string, callback: (options: ISelectOption[]) => void) =>
  action(RequirementsNpaActionsEnum.REQUIREMENTS_NPA_FIND_SELECT_OPTIONS_BY_QUERY, { query, callback });
